import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';

export default function OAuthCallback() {
    const navigate = useNavigate();
    const { handleCallback } = useAuth();
    const [error, setError] = useState(null);
    const handleCallbackRef = useRef(handleCallback);
    const processedRef = useRef(false);

    const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);

    useEffect(() => {
        handleCallbackRef.current = handleCallback;
    }, [handleCallback]);

    useEffect(() => {
        if (processedRef.current) return;
        processedRef.current = true;

        let mounted = true;
        let redirectTimer;

        const processCallback = async () => {
            try {
                const oauthError = searchParams.get('error');
                const oauthErrorDescription = searchParams.get('error_description');
                const code = searchParams.get('code');

                if (oauthError) {
                    throw new Error(oauthErrorDescription || `OAuth sign-in failed (${oauthError}).`);
                }

                if (!code) {
                    throw new Error('No authorization code found in callback URL');
                }

                // Exchange code for tokens via AuthProvider to ensure state update.
                await handleCallbackRef.current(code);

                // Redirect to dashboard on success.
                if (mounted) {
                    navigate('/dashboard', { replace: true });
                }
            } catch (err) {
                console.error('OAuth Callback Error:', err);
                if (mounted) {
                    setError(err.message || 'Authentication failed');
                    redirectTimer = setTimeout(() => navigate('/', { replace: true }), 3000);
                }
            }
        };

        processCallback();

        return () => {
            mounted = false;
            if (redirectTimer) clearTimeout(redirectTimer);
        };
    }, [navigate, searchParams]);

    if (error) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
                <h2>Authentication Error</h2>
                <p>{error}</p>
                <p>Redirecting to login...</p>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
            <h2>Authenticating...</h2>
            <p>Please wait while we complete the sign-in process.</p>
        </div>
    );
}
